import { Box } from "@mui/material";
import { muiTheme } from "@/styles/theme";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useState } from "react";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import OrganizationDropdown from "@/modules/common/components/organizations/organization-switch";
import OrganisationsActionsMobile from "@/modules/common/components/organizations/organisation-actions-mobile";
import ApplyOrganisationButton from "@/modules/common/components/organizations/apply-organisation-button";
import ResetOrganisationButton from "@/modules/common/components/organizations/reset-organisation-button";
import { useOrganization } from "@/modules/common//context/organizations/get-user-organizations.provider";
import { Organization } from "@/modules/common/dtos/index.v2";
import AccountManagersDropdown from "@/modules/common/components/organizations/account-managers-dropdown";
import { queryClient } from "@/queryClient";
import { FilterType, queriesToInvalidate } from "@/modules/common/constants";
import BecomeFranchiseToggle from "@/modules/common/components/organizations/become-franchise-toggle";
import useLocalStorageState from "@/modules/common/hooks/local-storage.hook";
import {
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";

interface OrganisationsActionsProps {
  isMultiple?: boolean;
  onReset?: () => void;
  onApply?: () => void;
}

const OrganisationsActions: React.FC<OrganisationsActionsProps> = ({
  isMultiple,
  onReset,
  onApply,
}) => {
  const [openOrganizationsModal, setOpenOrganizationsModal] = useState(false);
  const {
    setSelectedSingleOrganization,
    setShowSingleOrganizationTables,
    setFilterType,
    filterType,
    managedOrganizations,
  } = useOrganization();

  const [isBecomeFranchiseOnly, setIsBecomeFranchiseOnly] =
    useLocalStorageState("isBecomeFranchiseOnly", false);

  const handleResetSingleOrganisation = () => {
    setShowSingleOrganizationTables(false);
    localStorage.setItem(`selectedSingleOrganization`, "");
    setSelectedSingleOrganization({} as Organization);
    setFilterType(FilterType.NONE);
    setIsBecomeFranchiseOnly(false);
  };

  const handleApplySingleOrganisation = () => {
    setShowSingleOrganizationTables(true);
    setOpenOrganizationsModal(false);

    queriesToInvalidate.forEach((query) => {
      queryClient.refetchQueries(query);
    });
  };

  const screen = useScreenSize();
  return screen.isTablet || screen.isMobile ? (
    <Box sx={{ padding: "10px" }}>
      <OrganisationsActionsMobile
        openOrganizationsModal={openOrganizationsModal}
        setOpenOrganizationsModal={setOpenOrganizationsModal}
        filterType={filterType}
        setFilterType={setFilterType}
        isMultiple={isMultiple}
        isBecomeFranchiseOnly={isBecomeFranchiseOnly}
        setIsBecomeFranchiseOnly={setIsBecomeFranchiseOnly}
        cancelButton={
          <ResetOrganisationButton
            onReset={() => {
              onReset && onReset();
              handleResetSingleOrganisation();
              setIsBecomeFranchiseOnly(false);
              localStorage.removeItem("isBecomeFranchiseOnly");
            }}
          />
        }
        applyButton={
          <ApplyOrganisationButton
            setOpenOrganizationsModal={setOpenOrganizationsModal}
            onApply={onApply || handleApplySingleOrganisation}
          />
        }
      />
    </Box>
  ) : (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.10)",
          flexDirection: "row",
          backgroundColor: muiTheme.colorSchemes.light.palette.main.white,
          borderRadius: "10px",
          padding: "16px",
          gap: "24px",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <PermissionGuard
            permissions={[
              {
                name: PermissionsEnum.CROSS_ACCOUNTMANAGERS_SELECTION,
                level: PermissionLevel.READ,
              },
            ]}
          >
            {isMultiple && (
              <AccountManagersDropdown
                filterType={filterType}
                setFilterType={setFilterType}
                isBecomeFranchiseOnly={isBecomeFranchiseOnly}
              />
            )}
          </PermissionGuard>

          <OrganizationDropdown
            filterType={filterType}
            setFilterType={setFilterType}
            isMultiple={isMultiple}
            isBecomeFranchiseOnly={isBecomeFranchiseOnly}
          />
          {isMultiple &&
            managedOrganizations?.some(
              (org) =>
                org.organization?.tier?.name ===
                OrganizationTierTypes.FRANCHISE,
            ) && (
              <Box
                sx={{
                  whiteSpace: "nowrap",
                  display: "flex",
                  width: "100%",
                  alignSelf: "left",
                }}
              >
                <BecomeFranchiseToggle
                  isBecomeFranchiseOnly={isBecomeFranchiseOnly}
                  setIsBecomeFranchiseOnly={setIsBecomeFranchiseOnly}
                />
              </Box>
            )}
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: { mobile: "space-between", tablet: "flex-end" },
            gap: 2,
          }}
        >
          <ResetOrganisationButton
            onReset={() => {
              onReset && onReset();
              handleResetSingleOrganisation();
              setIsBecomeFranchiseOnly(false);
              localStorage.removeItem("isBecomeFranchiseOnly");
            }}
          />
          <ApplyOrganisationButton
            onApply={onApply || handleApplySingleOrganisation}
            setOpenOrganizationsModal={setOpenOrganizationsModal}
          />
        </Box>
      </Box>

      {openOrganizationsModal && (
        <ResponsiveEditDialog
          open={openOrganizationsModal}
          setOpen={setOpenOrganizationsModal}
          header="Organization Filters"
          modalFooter={false}
        >
          {isMultiple && (
            <AccountManagersDropdown
              filterType={filterType}
              setFilterType={setFilterType}
              isBecomeFranchiseOnly={isBecomeFranchiseOnly}
            />
          )}
          <OrganizationDropdown
            filterType={filterType}
            setFilterType={setFilterType}
            isMultiple={isMultiple}
            isBecomeFranchiseOnly={isBecomeFranchiseOnly}
          />
        </ResponsiveEditDialog>
      )}
    </>
  );
};

export default OrganisationsActions;
